import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiFolder, FiImage, FiRefreshCw, FiX } from "react-icons/fi";
import { usePermissions } from "../../Auth/PermissionsContext";
import GatedButton from "../../Components/GatedButton";
import { client } from "../../Clients";
import type { MediaBackupStatusDto, MediaJobStatusDto, MediaMonthSummaryDto, MediaUploadDryRunDto } from "./backupClient";
import MediaMonthBrowser from "./MediaMonthBrowser";
import UploadDropzone from "./UploadDropzone";
import { DataTable, Pill, SectionCard, extractApiError, formatBytes, formatDateTime } from "./ui";

/**
 * Section « Médias » de la page Sauvegardes : miroir mensuel du bucket média (images, PDF)
 * avec rotation en zip. Affiche l'état du mois courant, la liste des mois, un bouton de
 * synchronisation et une zone d'import : la restauration ÉCRASE le bucket, d'où la modale
 * rouge à confirmation tapée (ÉCRASER) précédée d'une analyse à blanc (dryRun).
 *
 * Le téléchargement du zip mensuel complet a été RETIRÉ de l'API (410) : chaque mois s'ouvre
 * désormais dans un navigateur de fichiers (« Parcourir ») : listing paginé + téléchargement
 * unitaire, en lecture seule (feature:backup:read).
 *
 * Le job média (sync / rotation / restauration) est unique côté serveur : la progression est
 * sondée toutes les ~1,5 s et, au montage, un job déjà en cours est ré-attaché au sondage
 * (même contrat que BodyMigrationCard). Le déclenchement de jobs (sync, restauration) exige
 * feature:backup:write (GatedButton / zone d'import désactivée).
 */

const BUSY_MESSAGE = "Une tâche média est déjà en cours.";
const REPUBLISH_REMINDER = "Pensez à republier le contenu pour propager les médias vers les sites publics.";

const MONTH_STATE_LABELS: Record<string, string> = {
	syncing: "en cours",
	compressing: "compression",
	archived: "archivé",
};

const JOB_KIND_LABELS: Record<string, string> = {
	sync: "Synchronisation",
	rotate: "Rotation",
	restore: "Restauration",
};

const monthStateLabel = (state: string): string => MONTH_STATE_LABELS[state] ?? state;
const monthStateTone = (state: string): "sky" | "amber" | "green" | "slate" =>
	state === "syncing" ? "sky" : state === "compressing" ? "amber" : state === "archived" ? "green" : "slate";

const WRITE_TOKEN = "feature:backup:write";

const MediaBackupSection: React.FC<{ pollIntervalMs?: number; browsePageSize?: number }> = ({
	pollIntervalMs = 1500,
	browsePageSize = 100,
}) => {
	const { canFeatureWrite } = usePermissions();
	const canWrite = canFeatureWrite("backup");
	const [status, setStatus] = useState<MediaBackupStatusDto | null>(null);
	const [months, setMonths] = useState<MediaMonthSummaryDto[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [message, setMessage] = useState<string | null>(null);
	const [job, setJob] = useState<MediaJobStatusDto | null>(null);
	const [jobActive, setJobActive] = useState(false);
	const [showRepublishBanner, setShowRepublishBanner] = useState(false);
	const [pendingFile, setPendingFile] = useState<File | null>(null);
	/** Mois ouvert dans le navigateur de fichiers (« Parcourir »). */
	const [browseMonth, setBrowseMonth] = useState<string | null>(null);
	const cancelledRef = useRef(false);

	useEffect(() => {
		cancelledRef.current = false;
		return () => {
			cancelledRef.current = true;
		};
	}, []);

	const refresh = useCallback(async () => {
		try {
			const [s, m] = await Promise.all([client.backup.mediaStatus(), client.backup.listMediaMonths()]);
			setStatus(s);
			setMonths(m);
			setError(null);
			return s;
		} catch (e) {
			setError(extractApiError(e));
			return null;
		}
	}, []);

	// Fin de job : récapitulatif en français selon le type, puis rafraîchissement des mois.
	const finishJob = useCallback(
		async (s: MediaJobStatusDto) => {
			setJob(s);
			setJobActive(false);
			if (s.state === "Failed") {
				setMessage(`Échec de la tâche média : ${s.error ?? "erreur inconnue"}.`);
			} else if (s.kind === "restore") {
				setMessage(`Restauration terminée : ${s.done} fichier(s) importé(s), ${s.failed} échec(s).`);
				setShowRepublishBanner(true);
			} else if (s.kind === "rotate") {
				setMessage(`Rotation terminée : ${s.done} mois traité(s), ${s.failed} échec(s).`);
			} else {
				setMessage(`Synchronisation terminée : ${s.done} objet(s) copié(s), ${s.failed} échec(s).`);
			}
			await refresh();
		},
		[refresh]
	);

	// Sondage du job unique. Seule l'OBSERVATION est bornée (30 min), pas le job lui-même.
	const pollJob = useCallback(async () => {
		const started = Date.now();
		const maxMs = 30 * 60 * 1000;
		while (!cancelledRef.current) {
			let s: MediaJobStatusDto | null = null;
			try {
				s = await client.backup.getMediaJobStatus();
			} catch {
				s = null;
			}
			if (cancelledRef.current) return;
			if (s) {
				setJob(s);
				if (s.state === "Completed" || s.state === "Failed") {
					await finishJob(s);
					return;
				}
			}
			if (Date.now() - started > maxMs) {
				setJobActive(false);
				setMessage("Toujours en cours. Le traitement continue côté serveur : revenez consulter le statut plus tard.");
				return;
			}
			await new Promise((r) => setTimeout(r, pollIntervalMs));
		}
	}, [finishJob, pollIntervalMs]);

	// Au montage : statut + mois, et ré-attachement au sondage si un job tourne déjà.
	useEffect(() => {
		let alive = true;
		(async () => {
			const s = await refresh();
			if (!alive || cancelledRef.current) return;
			if (s?.job?.state === "Running") {
				setJob(s.job);
				setJobActive(true);
				setMessage(null);
				void pollJob();
			}
		})();
		return () => {
			alive = false;
		};
	}, []);

	const startSync = useCallback(async () => {
		if (jobActive) return;
		setMessage(null);
		setShowRepublishBanner(false);
		try {
			const s = await client.backup.startMediaSync();
			setJob(s);
			setJobActive(true);
			void pollJob();
		} catch (e) {
			const statusCode = (e as { response?: { status?: number } })?.response?.status;
			setMessage(statusCode === 409 ? BUSY_MESSAGE : extractApiError(e));
		}
	}, [jobActive, pollJob]);

	// L'upload réel a été accepté (202) : la modale se ferme et la progression prend le relais.
	const onRestoreStarted = useCallback(
		(s: MediaJobStatusDto) => {
			setPendingFile(null);
			setMessage(null);
			setShowRepublishBanner(false);
			setJob(s);
			setJobActive(true);
			void pollJob();
		},
		[pollJob]
	);

	const sourceConfigured = status?.sourceConfigured ?? true;
	const targetConfigured = status?.targetConfigured ?? true;
	const monthStatus = status?.monthStatus;
	const total = job?.total ?? 0;
	const processed = (job?.done ?? 0) + (job?.failed ?? 0);
	const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

	return (
		<SectionCard
			icon={<FiImage className="h-5 w-5" aria-hidden />}
			title="Médias"
			description="Sauvegarde mensuelle du bucket média (images et documents) : miroir du mois courant, archives zip des mois passés."
		>
			{error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}

			{/* Statut du mois courant + drapeaux */}
			<div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-700">
						<span>
							Mois courant : <span className="font-semibold">{status?.month ?? "-"}</span>
						</span>
						<span>
							État :{" "}
							{monthStatus ? <Pill label={monthStateLabel(monthStatus.state)} tone={monthStateTone(monthStatus.state)} /> : <span className="text-slate-500">aucune synchronisation</span>}
						</span>
						<span>
							Dernière synchronisation : <span className="font-semibold">{formatDateTime(monthStatus?.lastSyncUtc)}</span>
						</span>
						<span className="text-xs text-slate-500">
							Source : {sourceConfigured ? "configurée" : "non configurée"} · Cible : {targetConfigured ? "configurée" : "non configurée"}
						</span>
					</div>
					<GatedButton
						token={WRITE_TOKEN}
						onClick={startSync}
						disabled={jobActive || !sourceConfigured}
						className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
					>
						<FiRefreshCw className="h-4 w-4" aria-hidden />
						{jobActive ? "Tâche en cours…" : "Synchroniser maintenant"}
					</GatedButton>
				</div>
				{!sourceConfigured && (
					<p className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
						La source médias (bucket interne) n'est pas configurée sur cette instance : la synchronisation est
						indisponible ici. Les mois déjà sauvegardés restent consultables et téléchargeables.
					</p>
				)}
			</div>

			{/* Progression du job en cours */}
			{jobActive && job && (
				<div className="space-y-2">
					<p className="text-sm font-medium text-slate-700">
						{JOB_KIND_LABELS[job.kind ?? ""] ?? "Tâche"} en cours…
						{job.currentKey && <span className="ml-2 text-xs font-normal text-slate-500">{job.currentKey}</span>}
					</p>
					<div
						className="h-2 w-full overflow-hidden rounded-full bg-slate-100"
						role="progressbar"
						aria-valuemin={0}
						aria-valuemax={total}
						aria-valuenow={processed}
					>
						<div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${pct}%` }} />
					</div>
					<p className="text-xs text-slate-600">
						{processed}/{total} fichier(s) : {job.done} traité(s) · {job.failed} échec(s)
					</p>
				</div>
			)}

			{message && <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">{message}</p>}

			{showRepublishBanner && (
				<div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
					{REPUBLISH_REMINDER}
				</div>
			)}

			{/* Mois sauvegardés : le zip complet n'est plus téléchargeable : navigation fichier par fichier. */}
			<div>
				<h3 className="text-sm font-semibold text-slate-700">Mois sauvegardés ({months.length})</h3>
				<DataTable
					headers={["Mois", "État", "Objets", "Taille", "Taille du zip", "Actions"]}
					rows={months.map((m) => ({
						key: m.month,
						cells: [
							<span className="font-medium">{m.month}</span>,
							<Pill label={monthStateLabel(m.state)} tone={monthStateTone(m.state)} />,
							String(m.objectCount),
							formatBytes(m.totalBytes),
							m.zipSizeBytes != null ? formatBytes(m.zipSizeBytes) : "-",
							<button
								type="button"
								onClick={() => setBrowseMonth(m.month)}
								title={`Parcourt les fichiers sauvegardés du mois ${m.month} (téléchargement unitaire)`}
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
							>
								<FiFolder className="h-3.5 w-3.5" aria-hidden />
								Parcourir
							</button>,
						],
					}))}
					emptyLabel="Aucun mois sauvegardé pour le moment : lancez une synchronisation."
				/>
			</div>

			{/* Import (restauration destructive) */}
			<div>
				<h3 className="text-sm font-semibold text-slate-700">Restaurer une archive médias</h3>
				<p className="mb-2 text-xs text-slate-500">
					Réinjecte le contenu d'un zip de sauvegarde dans le bucket média. Opération destructive : les fichiers
					existants portant les mêmes clés sont écrasés.
				</p>
				<UploadDropzone
					accept=".zip"
					label="Déposez une archive médias (.zip) ici, ou cliquez pour choisir un fichier"
					hint="Une analyse à blanc est effectuée avant toute écriture."
					inputLabel="Restaurer une archive médias (.zip)"
					disabled={jobActive || !targetConfigured || !canWrite}
					onFile={(file) => setPendingFile(file)}
				/>
				{!targetConfigured && (
					<p className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
						La cible de restauration (bucket interne) n'est pas configurée sur cette instance : l'import est indisponible ici.
					</p>
				)}
				{!canWrite && (
					<p className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
						La restauration nécessite la permission d'écriture sur les sauvegardes.
					</p>
				)}
			</div>

			{browseMonth && <MediaMonthBrowser month={browseMonth} pageSize={browsePageSize} onClose={() => setBrowseMonth(null)} />}

			{pendingFile && (
				<MediaRestoreModal file={pendingFile} onClose={() => setPendingFile(null)} onStarted={onRestoreStarted} />
			)}
		</SectionCard>
	);
};

/**
 * Modale rouge d'import médias : analyse à blanc (dryRun) dès l'ouverture pour afficher le
 * récapitulatif « X fichiers, Y Mo », puis confirmation tapée (ÉCRASER) avant l'upload réel.
 */
const MEDIA_CONFIRM_WORD = "ÉCRASER";

const MediaRestoreModal: React.FC<{
	file: File;
	onClose: () => void;
	onStarted: (job: MediaJobStatusDto) => void;
}> = ({ file, onClose, onStarted }) => {
	const [typed, setTyped] = useState("");
	const [dryRun, setDryRun] = useState<MediaUploadDryRunDto | null>(null);
	const [analysing, setAnalysing] = useState(true);
	const [uploadPct, setUploadPct] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);

	const confirmed = typed.normalize("NFC").trim() === MEDIA_CONFIRM_WORD;
	const busy = uploadPct != null;

	// Analyse à blanc dès l'ouverture : le serveur valide le zip et annonce ce qui serait restauré.
	useEffect(() => {
		let alive = true;
		(async () => {
			try {
				const result = (await client.backup.uploadMediaZip(file, { dryRun: true })) as MediaUploadDryRunDto;
				if (alive) setDryRun(result);
			} catch (e) {
				if (alive) setError(extractApiError(e));
			} finally {
				if (alive) setAnalysing(false);
			}
		})();
		return () => {
			alive = false;
		};
	}, [file]);

	const runUpload = async () => {
		if (!confirmed || busy || !dryRun) return;
		setError(null);
		setUploadPct(0);
		try {
			const job = (await client.backup.uploadMediaZip(file, {
				onUploadProgress: (event) => {
					if (event.total) setUploadPct(Math.round((event.loaded / event.total) * 100));
				},
			})) as MediaJobStatusDto;
			onStarted(job);
		} catch (e) {
			const statusCode = (e as { response?: { status?: number } })?.response?.status;
			setError(statusCode === 409 ? BUSY_MESSAGE : extractApiError(e));
			setUploadPct(null);
		}
	};

	return (
		<div className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-900/50 p-4" role="dialog" aria-modal="true" aria-label="Restauration des médias">
			<div className="w-full max-w-lg rounded-2xl border-2 border-red-300 bg-white p-5 shadow-xl">
				<div className="flex items-start justify-between gap-3">
					<div className="flex items-center gap-2">
						<span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-red-100 text-red-600">
							<FiAlertTriangle className="h-5 w-5" aria-hidden />
						</span>
						<h3 className="text-base font-semibold text-red-700">Restauration des médias</h3>
					</div>
					<button
						type="button"
						onClick={onClose}
						disabled={busy}
						aria-label="Fermer"
						className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
					>
						<FiX className="h-5 w-5" aria-hidden />
					</button>
				</div>

				<p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
					⚠️ Les fichiers du bucket média seront ÉCRASÉS par le contenu de l'archive.
				</p>

				<p className="mt-3 text-sm text-slate-600">
					Archive : <code className="rounded bg-slate-100 px-1 text-xs">{file.name}</code>
				</p>

				{analysing && <p className="mt-3 text-sm text-slate-600">Analyse de l'archive en cours…</p>}
				{dryRun && (
					<p className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
						Contenu vérifié :{" "}
						<span className="font-semibold">
							{dryRun.entries} fichiers, {(dryRun.totalBytes / 1048576).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} Mo
						</span>
						.
					</p>
				)}

				{error && <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}

				{!analysing && dryRun && (
					<label className="mt-4 block text-sm text-slate-700">
						Pour confirmer, tapez <span className="font-semibold">{MEDIA_CONFIRM_WORD}</span> :
						<input
							type="text"
							value={typed}
							onChange={(e) => setTyped(e.target.value)}
							disabled={busy}
							placeholder={MEDIA_CONFIRM_WORD}
							className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-red-400 focus:outline-none"
						/>
					</label>
				)}

				{busy && (
					<div className="mt-3 space-y-1">
						<div className="h-2 w-full overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={uploadPct ?? 0}>
							<div className="h-full rounded-full bg-red-500 transition-all" style={{ width: `${uploadPct ?? 0}%` }} />
						</div>
						<p className="text-xs text-slate-600">Envoi de l'archive… {uploadPct ?? 0} %</p>
					</div>
				)}

				<div className="mt-4 flex justify-end gap-2">
					<button
						type="button"
						onClick={onClose}
						disabled={busy}
						className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={runUpload}
						disabled={!confirmed || busy || analysing || !dryRun}
						className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{busy ? "Envoi en cours…" : "Écraser et restaurer"}
					</button>
				</div>
			</div>
		</div>
	);
};

export default MediaBackupSection;
